import React from "react";
import "./app.css";

import { setTranslations } from "polotno/config";

import fr from "./shared/translations/fr.json";
import en from "./shared/translations/en.json";
import id from "./shared/translations/id.json";
import ru from "./shared/translations/ru.json";
import ptBr from "./shared/translations/pt-br.json";
import zhCh from "./shared/translations/zh-ch.json";

import { useProject } from "./shared/utils/project";
import { observer } from "mobx-react-lite";
import { StoreType } from "polotno/model/store";
import { useHeight } from "./functions/hooks/use-height";
import { loadFile } from "./shared/utils/file";

import { PolotnoContainer, SidePanelWrap, WorkspaceWrap } from "polotno";
import { Workspace } from "polotno/canvas/workspace";
import { Toolbar } from "polotno/toolbar/toolbar";
import { SidePanel } from "polotno/side-panel";
import { TimelineControl } from "./components/timeline/TimelineControl";
import { TranscriptTab } from "./components/transcript/TranscriptTab";
import { MainWorkspace } from "./components/workspace/MainWorkspace";
import LoadingOverlay from "./components/common/LoadingOverlay";
import { VideosSection } from "./components/sections/video-section";
import { ToolbarControls } from "./toolbar-controls";
import { LearningModeTab } from "./components/learning-mode/LearningModeTab";
import { styled } from "styled-components";
import { UploadSection } from "./components/sections/upload-section";

// load default translations
setTranslations(en);

type Props = Readonly<{
  store: StoreType;
}>;

const unusedTabs = [
  "templates",
  // "photos",
  "elements",
  "layers",
  "background",
  "size",
];

const sections = [
  UploadSection,
  TranscriptTab,
  LearningModeTab,
  VideosSection,
].filter((section) => !unusedTabs.includes(section.name));

const App = observer(({ store }: Props) => {
  const project = useProject();
  const height = useHeight();

  React.useEffect(() => {
    if (project.language.startsWith("fr")) {
      setTranslations(fr, { validate: true });
    } else if (project.language.startsWith("id")) {
      setTranslations(id, { validate: true });
    } else if (project.language.startsWith("ru")) {
      setTranslations(ru, { validate: true });
    } else if (project.language.startsWith("pt")) {
      setTranslations(ptBr, { validate: true });
    } else if (project.language.startsWith("zh")) {
      setTranslations(zhCh, { validate: true });
    } else {
      setTranslations(en, { validate: true });
    }
  }, [project.language]);

  React.useEffect(() => {
    project.firstLoad();
  }, [project]);

  const handleDrop = (ev: React.DragEvent) => {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();

    // skip the case if we dropped DOM element from side panel
    // in that case Safari will have more data in "items"
    if (ev.dataTransfer?.files.length !== ev.dataTransfer?.items.length) {
      return;
    }
    // Use DataTransfer interface to access the file(s)
    if (!ev.dataTransfer?.files) return;
    for (let i = 0; i < ev.dataTransfer.files.length; i++) {
      loadFile(ev.dataTransfer.files[i], store);
    }
  };

  return (
    <div
      style={{
        width: "100vw",
        height: height + "px",
        display: "flex",
        flexDirection: "column",
      }}
      onDrop={handleDrop}
    >
      <div style={{ height: "100%" }}>
        <PolotnoContainer className="polotno-app-container">
          <SidePanelWrapper>
            <SidePanel
              store={store}
              sections={sections}
              defaultSection="upload"
            />
          </SidePanelWrapper>
          <WorkspaceWrap>
            <Toolbar store={store} components={ToolbarControls} />
            {/* This default polotno workspace is hidden */}
            <Workspace
              components={{ PageControls: () => null }}
              renderOnlyActivePage
              store={store}
            />
            {/* Main workspace of this app */}
            <MainWorkspace store={store} />
            <TimelineControl store={store} />
          </WorkspaceWrap>
        </PolotnoContainer>
      </div>
      {project.status === "loading" && <LoadingOverlay />}
    </div>
  );
});

export default App;

const SidePanelWrapper = styled(SidePanelWrap)`
  height: 100% !important;
`;
